import { Delivery } from '../../models/delivery.model';
import { WorkflowDetailsHeaderProps } from '../../../../ui/components/pure/workflow-details-header/workflow-details-header.component';
import { createPath, Paths } from '../../../../app/router/app.router';
import { DeliveriesCardProps } from '../components/deliveries-card.component';
import { decodeBarcode } from '../../../../ui/util/barcode';
import { DeliveryDetailsActions, makeDefineDeliveryDetailsActions } from '../actions/deliveries-details.actions';
import {
  KnownDeliveredByAgentDtoType,
  KnownDeliveryState,
  KnownHubDtoType,
  KnownPartyDtoType,
  KnownPartyParentDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { TimelineEventProps } from '../../../../ui/components/pure/timeline/timeline.types';
import { StatusCollected } from '../../../../ui/components/pure/icons/StatusCollected.icon';
import { Colors } from '../../../../ui/theme/colors';
import { getDeliveryStateLabelForProject } from '../../../../core/util/status-label.util';
import { BookingFlowType } from '../../../../ui/enums/booking-flow-type.enum';

export function makeDeliveryDetailsHeaderProps(
  projectID: string,
  delivery: Delivery,
  setAction: (action: DeliveryDetailsActions) => void,
  bookingFlowType: BookingFlowType
): WorkflowDetailsHeaderProps {
  return {
    workflowType: delivery.deliveryTypeLabel,
    WorkflowIcon: delivery.deliveryTypeIcon,
    workflowStatus: getDeliveryStateLabelForProject(delivery.state, bookingFlowType),
    bgColor: delivery.stateColor,
    workflowReference: delivery.deliveryReference,
    actions: makeDefineDeliveryDetailsActions(delivery, projectID, bookingFlowType)(setAction),
    isDeleted: delivery.isDeleted
  };
}

export function makeDeliveryDetailsCardProps(projectID: string, delivery: Delivery): DeliveriesCardProps {
  const { party: recipient, parent } = delivery.deliveredTo || {};
  const { agent, party: deliveredBy } = delivery.deliveredBy || {};
  const { project } = delivery;

  return {
    recipient: recipient
      ? {
          id: recipient.id,
          type: recipient.type,
          name: recipient.name,
          link:
            parent?.type === KnownPartyParentDtoType.Group
              ? createPath(Paths.customer, { projectID, userID: recipient.id })
              : parent?.type === KnownPartyParentDtoType.Project
              ? createPath(Paths.teamMember, { projectID, teamMemberID: recipient.id })
              : undefined,
          // As `Delivery.unitLabel` decides it: only occupants and customers have a unit.
          unit:
            parent && (recipient.type === KnownPartyDtoType.Occupant || recipient.type === KnownPartyDtoType.Customer)
              ? { id: parent.id, name: parent.name }
              : undefined
        }
      : undefined,
    project: { id: project.id, name: project.name, projectType: project.projectType, address: project.address },
    // The delivery names neither one's own project, so both open through the viewer's. The card
    // links them for internal users only, who can read any project.
    deliveredBy: deliveredBy
      ? {
          id: deliveredBy.id,
          type: deliveredBy.type,
          name: deliveredBy.name,
          imageURL: deliveredBy.imageURL,
          link:
            deliveredBy.type === KnownPartyDtoType.Carrier && deliveredBy.id
              ? createPath(Paths.carrier, { projectID, carrierID: deliveredBy.id })
              : undefined
        }
      : undefined,
    courier:
      agent?.type === KnownDeliveredByAgentDtoType.Courier && agent.id
        ? { id: agent.id, name: agent.name, link: createPath(Paths.courier, { projectID, courierID: agent.id }) }
        : undefined,
    carrierReference: delivery.carrierReference,
    barcode: decodeBarcode(delivery.barcode)
  };
}

export function makeDeliveryTimelineCurrentAction(
  delivery: Delivery | undefined,
  setAction: (action: DeliveryDetailsActions) => void
): TimelineEventProps | undefined {
  if (!delivery) {
    return undefined;
  }
  if (delivery.deliveredAtHub?.type !== KnownHubDtoType.KiosklessSpacesHub) {
    return undefined;
  }
  if (![KnownDeliveryState.Delivered, KnownDeliveryState.Overdue].includes(delivery.state)) {
    return undefined;
  }

  return {
    title: 'Awaiting Collection',
    icon: {
      color: delivery.stateColor,
      Icon: StatusCollected
    },
    action: {
      buttonLabel: 'Mark Collected',
      buttonColor: Colors.collectedGreen,
      onClick: () => setAction(DeliveryDetailsActions.MARK_COLLECTED)
    },
    options: {
      dividerStyle: 'dashed',
      alignTitle: 'middle'
    }
  };
}
